import axiosInstance from '../../../axiosInstance'
import apiEndpoints from '../../../api'
import { notify } from '../../../../utils/notification'
import { selectedSocietyStorer } from '../../../../utils/webStorer'

// The task API resolves the active society from (in order) society_id in
// path/body/query, then the X-Society-Id header. We send the header on every
// request (most reliable) and also pass society_id in body/query where natural.
const societyId = () => selectedSocietyStorer.getSocietyId();

const withSociety = (config = {}) => ({
    ...config,
    headers: {
        ...(config.headers || {}),
        'X-Society-Id': societyId(),
    },
});

// `silent` suppresses the success toast — used by the comment thread, where a
// popup on every message is noise. Errors are still surfaced by `fail`.
const ok = (response, message, silent) => {
    if (!silent && response?.success && message) {
        notify('success', response.message || message);
    }
    return response;
};

const fail = (errors) => {
    console.log('errors', errors);
    notify('error', errors.response?.data?.message || errors?.message || 'Something went wrong');
    return errors;
};

// ============ TASKS ============

export const getTasksListReq = (action) => {
    const d = action.data || {};
    const params = new URLSearchParams();
    params.append('page', d.page || 1);
    params.append('limit', d.limit || 20);
    if (d.status) params.append('status', d.status);
    if (d.priority) params.append('priority', d.priority);
    if (d.department) params.append('department', d.department);
    if (d.category) params.append('category', d.category);
    if (d.assigned_to) params.append('assigned_to', d.assigned_to);
    if (d.assignee_any) params.append('assignee_any', d.assignee_any);
    if (d.created_by) params.append('created_by', d.created_by);
    if (d.vendor_id) params.append('vendor_id', d.vendor_id);
    if (d.flat_id) params.append('flat_id', d.flat_id);
    if (d.project_id) params.append('project_id', d.project_id);
    if (d.due) params.append('due', d.due);
    if (d.from_date) params.append('from_date', d.from_date);
    if (d.to_date) params.append('to_date', d.to_date);
    if (d.search) params.append('search', d.search);
    if (d.include_archived) params.append('include_archived', d.include_archived);
    if (d.sort_by) params.append('sort_by', d.sort_by);
    if (d.sort_dir) params.append('sort_dir', d.sort_dir);
    const sid = societyId();
    if (sid) params.append('society_id', sid);

    const url = `${apiEndpoints.SOCIETY_GET_TASKS_LIST_API}?${params.toString()}`;
    return axiosInstance.get(url, withSociety())
        .then(response => response)
        .catch(fail);
};

export const createTaskReq = (action) => {
    const payload = { ...action.data, society_id: societyId() };
    delete payload.onSuccess;
    return axiosInstance.post(apiEndpoints.SOCIETY_CREATE_TASK_API, payload, withSociety())
        .then(response => ok(response, 'Task created successfully'))
        .catch(fail);
};

export const getTaskDetailsReq = (action) => {
    const url = apiEndpoints.SOCIETY_GET_TASK_DETAILS_API.replace(':id', action.data.taskId);
    return axiosInstance.get(url, withSociety())
        .then(response => response)
        .catch(fail);
};

export const updateTaskReq = (action) => {
    const url = apiEndpoints.SOCIETY_UPDATE_TASK_API.replace(':id', action.data.taskId);
    const payload = { ...action.data, society_id: societyId() };
    delete payload.taskId;
    delete payload.onSuccess;
    return axiosInstance.put(url, payload, withSociety())
        .then(response => ok(response, 'Task updated successfully'))
        .catch(fail);
};

export const deleteTaskReq = (action) => {
    const url = apiEndpoints.SOCIETY_DELETE_TASK_API.replace(':id', action.data.taskId);
    return axiosInstance.delete(url, withSociety({ data: { society_id: societyId() } }))
        .then(response => ok(response, 'Task deleted successfully'))
        .catch(fail);
};

export const cloneTaskReq = (action) => {
    const url = apiEndpoints.SOCIETY_CLONE_TASK_API.replace(':id', action.data.taskId);
    const payload = {
        title: action.data.title,
        due_date: action.data.due_date,
        copy_assignees: action.data.copy_assignees,
        society_id: societyId(),
    };
    return axiosInstance.post(url, payload, withSociety())
        .then(response => ok(response, 'Task cloned successfully'))
        .catch(fail);
};

export const getTaskHistoryReq = (action) => {
    const url = apiEndpoints.SOCIETY_GET_TASK_HISTORY_API.replace(':id', action.data.taskId);
    return axiosInstance.get(url, withSociety())
        .then(response => response)
        .catch(fail);
};

// ============ WORKFLOW ============

export const transitionTaskReq = (action) => {
    const url = apiEndpoints.SOCIETY_TRANSITION_TASK_API.replace(':id', action.data.taskId);
    const payload = { status: action.data.status, note: action.data.note || undefined, society_id: societyId() };
    return axiosInstance.post(url, payload, withSociety())
        .then(response => ok(response, 'Status updated successfully'))
        .catch(fail);
};

export const changeTaskPriorityReq = (action) => {
    const url = apiEndpoints.SOCIETY_CHANGE_TASK_PRIORITY_API.replace(':id', action.data.taskId);
    const payload = { priority: action.data.priority, society_id: societyId() };
    return axiosInstance.patch(url, payload, withSociety())
        .then(response => ok(response, 'Priority updated successfully'))
        .catch(fail);
};

export const changeTaskTitleReq = (action) => {
    const url = apiEndpoints.SOCIETY_CHANGE_TASK_TITLE_API.replace(':id', action.data.taskId);
    const payload = { title: action.data.title, society_id: societyId() };
    return axiosInstance.patch(url, payload, withSociety())
        .then(response => ok(response, 'Title updated successfully'))
        .catch(fail);
};

// ============ ASSIGNMENT ============

export const assignTaskReq = (action) => {
    const url = apiEndpoints.SOCIETY_ASSIGN_TASK_API.replace(':id', action.data.taskId);
    const payload = { society_id: societyId(), role: action.data.role || 'ASSIGNEE' };
    if (action.data.user_ids) payload.user_ids = action.data.user_ids;
    if (action.data.user_id) payload.user_id = action.data.user_id;
    return axiosInstance.post(url, payload, withSociety())
        .then(response => ok(response, 'Task assigned successfully'))
        .catch(fail);
};

export const selfAssignTaskReq = (action) => {
    const url = apiEndpoints.SOCIETY_SELF_ASSIGN_TASK_API.replace(':id', action.data.taskId);
    return axiosInstance.post(url, { society_id: societyId() }, withSociety())
        .then(response => ok(response, 'Task assigned to you'))
        .catch(fail);
};

export const reassignTaskReq = (action) => {
    const url = apiEndpoints.SOCIETY_REASSIGN_TASK_API.replace(':id', action.data.taskId);
    const payload = { to_user_id: action.data.to_user_id, note: action.data.note || undefined, society_id: societyId() };
    return axiosInstance.post(url, payload, withSociety())
        .then(response => ok(response, 'Task reassigned successfully'))
        .catch(fail);
};

export const delegateTaskReq = (action) => {
    const url = apiEndpoints.SOCIETY_DELEGATE_TASK_API.replace(':id', action.data.taskId);
    const payload = { to_user_id: action.data.to_user_id, note: action.data.note || undefined, society_id: societyId() };
    return axiosInstance.post(url, payload, withSociety())
        .then(response => ok(response, 'Task delegated successfully'))
        .catch(fail);
};

export const removeAssigneeReq = (action) => {
    const url = apiEndpoints.SOCIETY_REMOVE_TASK_ASSIGNEE_API
        .replace(':id', action.data.taskId)
        .replace(':userId', action.data.userId);
    return axiosInstance.delete(url, withSociety({ data: { society_id: societyId() } }))
        .then(response => ok(response, 'Assignee removed'))
        .catch(fail);
};

// ============ WATCHERS ============

export const addWatcherReq = (action) => {
    const url = apiEndpoints.SOCIETY_ADD_TASK_WATCHER_API.replace(':id', action.data.taskId);
    const payload = { society_id: societyId() };
    if (action.data.user_id) payload.user_id = action.data.user_id;
    return axiosInstance.post(url, payload, withSociety())
        .then(response => ok(response, 'Watcher added'))
        .catch(fail);
};

export const removeWatcherReq = (action) => {
    const url = apiEndpoints.SOCIETY_REMOVE_TASK_WATCHER_API
        .replace(':id', action.data.taskId)
        .replace(':userId', action.data.userId);
    return axiosInstance.delete(url, withSociety({ data: { society_id: societyId() } }))
        .then(response => ok(response, 'Watcher removed'))
        .catch(fail);
};

// ============ COMMENTS ============

export const getCommentsReq = (action) => {
    const url = apiEndpoints.SOCIETY_GET_TASK_COMMENTS_API.replace(':id', action.data.taskId);
    const params = new URLSearchParams();
    if (action.data.include_internal === false) params.append('include_internal', 'false');
    const sid = societyId();
    if (sid) params.append('society_id', sid);
    const qs = params.toString();
    return axiosInstance.get(qs ? `${url}?${qs}` : url, withSociety())
        .then(response => response)
        .catch(fail);
};

// A comment can be posted with up to 10 attachments in the same request. With
// files we must send multipart (field `attachments`, repeated once per file) —
// the society context comes from the X-Society-Id header because a form field
// is not visible to the middleware that runs before the file parser.
export const addCommentReq = (action) => {
    const url = apiEndpoints.SOCIETY_ADD_TASK_COMMENT_API.replace(':id', action.data.taskId);
    const files = Array.isArray(action.data.files) ? action.data.files : [];

    if (files.length) {
        const formData = new FormData();
        formData.append('body', action.data.body);
        formData.append('is_internal', String(!!action.data.is_internal));
        if (action.data.parent_comment_id) formData.append('parent_comment_id', String(action.data.parent_comment_id));
        // Mentions go as a comma-separated list, not repeated fields.
        if (action.data.mentioned_user_ids?.length) formData.append('mentioned_user_ids', action.data.mentioned_user_ids.join(','));
        files.forEach((f) => formData.append('attachments', f));
        return axiosInstance.post(url, formData, withSociety())
            .then(response => ok(response, 'Comment added', action.data.silent))
            .catch(fail);
    }

    const payload = {
        body: action.data.body,
        is_internal: action.data.is_internal || false,
        parent_comment_id: action.data.parent_comment_id || undefined,
        mentioned_user_ids: action.data.mentioned_user_ids || undefined,
        society_id: societyId(),
    };
    return axiosInstance.post(url, payload, withSociety())
        .then(response => ok(response, 'Comment added', action.data.silent))
        .catch(fail);
};

// Attach more files to a comment that already exists.
export const uploadCommentAttachmentsReq = (action) => {
    const url = apiEndpoints.SOCIETY_ADD_TASK_COMMENT_ATTACHMENTS_API
        .replace(':id', action.data.taskId)
        .replace(':commentId', action.data.commentId);
    const formData = new FormData();
    (action.data.files || []).forEach((f) => formData.append('attachments', f));
    return axiosInstance.post(url, formData, withSociety())
        .then(response => ok(response, 'Attachments uploaded', action.data.silent))
        .catch(fail);
};

// ============ CHECKLIST ============

export const addChecklistItemReq = (action) => {
    const url = apiEndpoints.SOCIETY_ADD_TASK_CHECKLIST_API.replace(':id', action.data.taskId);
    return axiosInstance.post(url, { title: action.data.title, society_id: societyId() }, withSociety())
        .then(response => ok(response, 'Checklist item added'))
        .catch(fail);
};

export const toggleChecklistItemReq = (action) => {
    const url = apiEndpoints.SOCIETY_TOGGLE_TASK_CHECKLIST_API
        .replace(':id', action.data.taskId)
        .replace(':itemId', action.data.itemId);
    return axiosInstance.patch(url, { is_done: action.data.is_done, society_id: societyId() }, withSociety())
        .then(response => response)
        .catch(fail);
};

export const deleteChecklistItemReq = (action) => {
    const url = apiEndpoints.SOCIETY_DELETE_TASK_CHECKLIST_API
        .replace(':id', action.data.taskId)
        .replace(':itemId', action.data.itemId);
    return axiosInstance.delete(url, withSociety({ data: { society_id: societyId() } }))
        .then(response => ok(response, 'Checklist item removed'))
        .catch(fail);
};

// ============ DEPENDENCIES ============

export const addDependencyReq = (action) => {
    const url = apiEndpoints.SOCIETY_ADD_TASK_DEPENDENCY_API.replace(':id', action.data.taskId);
    const payload = {
        depends_on_task_id: action.data.depends_on_task_id,
        type: action.data.type || 'BLOCKED_BY',
        society_id: societyId(),
    };
    return axiosInstance.post(url, payload, withSociety())
        .then(response => ok(response, 'Dependency added'))
        .catch(fail);
};

export const removeDependencyReq = (action) => {
    const url = apiEndpoints.SOCIETY_REMOVE_TASK_DEPENDENCY_API
        .replace(':id', action.data.taskId)
        .replace(':dependencyId', action.data.dependencyId);
    return axiosInstance.delete(url, withSociety({ data: { society_id: societyId() } }))
        .then(response => ok(response, 'Dependency removed'))
        .catch(fail);
};

// ============ ATTACHMENTS ============

export const uploadAttachmentReq = (action) => {
    const url = apiEndpoints.SOCIETY_UPLOAD_TASK_ATTACHMENT_API.replace(':id', action.data.taskId);
    const formData = new FormData();
    formData.append('attachment', action.data.file);
    if (action.data.comment_id) formData.append('comment_id', action.data.comment_id);
    // Let the browser set the multipart boundary — only pass the society header.
    return axiosInstance.post(url, formData, withSociety())
        .then(response => ok(response, 'Attachment uploaded'))
        .catch(fail);
};

// Returns the raw file blob (responseType 'blob'); the caller triggers the
// browser download. Bypasses the ok/fail wrappers used by JSON endpoints.
export const downloadAttachmentReq = ({ taskId, attachmentId, onDownloadProgress }) => {
    const url = apiEndpoints.SOCIETY_DOWNLOAD_TASK_ATTACHMENT_API
        .replace(':id', taskId)
        .replace(':attachmentId', attachmentId);
    return axiosInstance.get(url, withSociety({ responseType: 'blob', onDownloadProgress }));
};

export const deleteAttachmentReq = (action) => {
    const url = apiEndpoints.SOCIETY_DELETE_TASK_ATTACHMENT_API
        .replace(':id', action.data.taskId)
        .replace(':attachmentId', action.data.attachmentId);
    return axiosInstance.delete(url, withSociety({ data: { society_id: societyId() } }))
        .then(response => ok(response, 'Attachment deleted', action.data.silent))
        .catch(fail);
};

// ============ PROJECTS ============

export const getProjectsListReq = (action) => {
    const d = action.data || {};
    const params = new URLSearchParams();
    if (d.status) params.append('status', d.status);
    if (d.search) params.append('search', d.search);
    const sid = societyId();
    if (sid) params.append('society_id', sid);
    const qs = params.toString();
    const url = qs ? `${apiEndpoints.SOCIETY_GET_TASK_PROJECTS_API}?${qs}` : apiEndpoints.SOCIETY_GET_TASK_PROJECTS_API;
    return axiosInstance.get(url, withSociety())
        .then(response => response)
        .catch(fail);
};

export const createProjectReq = (action) => {
    const payload = { ...action.data, society_id: societyId() };
    delete payload.onSuccess;
    return axiosInstance.post(apiEndpoints.SOCIETY_CREATE_TASK_PROJECT_API, payload, withSociety())
        .then(response => ok(response, 'Project created successfully'))
        .catch(fail);
};

export const getProjectDetailsReq = (action) => {
    const url = apiEndpoints.SOCIETY_GET_TASK_PROJECT_DETAILS_API.replace(':projectId', action.data.projectId);
    return axiosInstance.get(url, withSociety())
        .then(response => response)
        .catch(fail);
};

export const updateProjectReq = (action) => {
    const url = apiEndpoints.SOCIETY_UPDATE_TASK_PROJECT_API.replace(':projectId', action.data.projectId);
    const payload = { ...action.data, society_id: societyId() };
    delete payload.projectId;
    delete payload.onSuccess;
    return axiosInstance.put(url, payload, withSociety())
        .then(response => ok(response, 'Project updated successfully'))
        .catch(fail);
};

export const deleteProjectReq = (action) => {
    const url = apiEndpoints.SOCIETY_DELETE_TASK_PROJECT_API.replace(':projectId', action.data.projectId);
    return axiosInstance.delete(url, withSociety({ data: { society_id: societyId() } }))
        .then(response => ok(response, 'Project deleted successfully'))
        .catch(fail);
};

// ============ COLLECTION ============

export const bulkCreateTasksReq = (action) => {
    const payload = { tasks: action.data.tasks, society_id: societyId() };
    return axiosInstance.post(apiEndpoints.SOCIETY_BULK_CREATE_TASKS_API, payload, withSociety())
        .then(response => ok(response, 'Tasks created successfully'))
        .catch(fail);
};

export const getTaskDashboardReq = (action) => {
    const d = action?.data || {};
    const params = new URLSearchParams();
    if (d.assigned_to) params.append('assigned_to', d.assigned_to);
    if (d.project_id) params.append('project_id', d.project_id);
    if (d.department) params.append('department', d.department);
    if (d.from_date) params.append('from_date', d.from_date);
    if (d.to_date) params.append('to_date', d.to_date);
    if (d.assignee_limit) params.append('assignee_limit', d.assignee_limit);
    if (d.project_limit) params.append('project_limit', d.project_limit);
    const sid = societyId();
    if (sid) params.append('society_id', sid);
    const qs = params.toString();
    const url = qs ? `${apiEndpoints.SOCIETY_GET_TASK_DASHBOARD_API}?${qs}` : apiEndpoints.SOCIETY_GET_TASK_DASHBOARD_API;
    return axiosInstance.get(url, withSociety())
        .then(response => response)
        .catch(fail);
};
